import React, { useEffect, useState } from 'react';
import { ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import { BottomSheet } from '../ui/BottomSheet';
import type { DerivedClientStatus } from './clientStatus';
import type { ClientSortField, FamilyCountFilter } from './ClientFiltersToolbar';

export interface ClientFiltersSheetProps {
  open: boolean;
  onClose: () => void;
  planId: string;
  onPlanChange: (id: string) => void;
  planOptions: { id: string; name: string }[];
  gremio: string;
  onGremioChange: (v: string) => void;
  gremioOptions: string[];
  status: DerivedClientStatus | 'all';
  onStatusChange: (v: DerivedClientStatus | 'all') => void;
  family: FamilyCountFilter;
  onFamilyChange: (v: FamilyCountFilter) => void;
  sortField: ClientSortField;
  sortDir: 'asc' | 'desc';
  onSortFieldChange: (f: ClientSortField) => void;
  onSortDirChange: (d: 'asc' | 'desc') => void;
  onApply: () => void;
  onClear: () => void;
}

const STATUS_OPTIONS: { value: DerivedClientStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'activo', label: 'Activo' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'moroso', label: 'Moroso' },
  { value: 'suspendido', label: 'Suspendido' },
  { value: 'inactivo', label: 'Inactivo' },
];

const FAMILY_OPTIONS: { value: FamilyCountFilter; label: string }[] = [
  { value: 'all', label: 'Familiares (todos)' },
  { value: 'none', label: 'Sin familiares' },
  { value: 'low', label: '1 a 3 familiares' },
  { value: 'high', label: '4 o más' },
];

const SORT_OPTIONS: { value: ClientSortField; label: string }[] = [
  { value: 'name', label: 'Nombre (A - Z)' },
  { value: 'date', label: 'Alta (fecha)' },
  { value: 'plan', label: 'Plan' },
  { value: 'family', label: 'Nº familiares' },
];

const selectCls =
  'h-11 w-full rounded-xl border border-[#E5EAF1] bg-white px-3 text-sm font-medium text-slate-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200';

export const ClientFiltersSheet: React.FC<ClientFiltersSheetProps> = ({
  open,
  onClose,
  planId,
  onPlanChange,
  planOptions,
  gremio,
  onGremioChange,
  gremioOptions,
  status,
  onStatusChange,
  family,
  onFamilyChange,
  sortField,
  sortDir,
  onSortFieldChange,
  onSortDirChange,
  onApply,
  onClear,
}) => {
  const [draft, setDraft] = useState({
    planId,
    gremio,
    status,
    family,
    sortField,
    sortDir,
  });

  useEffect(() => {
    if (open) {
      setDraft({ planId, gremio, status, family, sortField, sortDir });
    }
  }, [open, planId, gremio, status, family, sortField, sortDir]);

  const apply = () => {
    onPlanChange(draft.planId);
    onGremioChange(draft.gremio);
    onStatusChange(draft.status);
    onFamilyChange(draft.family);
    onSortFieldChange(draft.sortField);
    onSortDirChange(draft.sortDir);
    onApply();
    onClose();
  };

  const clear = () => {
    setDraft({
      planId: '',
      gremio: '',
      status: 'all',
      family: 'all',
      sortField: 'name',
      sortDir: 'asc',
    });
    onClear();
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Filtros"
      footer={
        <div className="space-y-2 pb-1">
          <button
            type="button"
            onClick={apply}
            className="flex h-12 w-full items-center justify-center rounded-xl bg-primary-600 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 active:scale-[0.99]"
          >
            Aplicar filtros
          </button>
          <button
            type="button"
            onClick={() => {
              clear();
              onClose();
            }}
            className="flex h-10 w-full items-center justify-center text-sm font-medium text-slate-500 transition hover:text-slate-800"
          >
            Limpiar filtros
          </button>
        </div>
      }
    >
      <div className="space-y-4 py-1">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Plan</span>
          <select
            className={selectCls}
            value={draft.planId}
            onChange={(e) => setDraft((d) => ({ ...d, planId: e.target.value }))}
          >
            <option value="">Todos los planes</option>
            {planOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Gremio</span>
          <select
            className={selectCls}
            value={draft.gremio}
            onChange={(e) => setDraft((d) => ({ ...d, gremio: e.target.value }))}
          >
            <option value="">Todos</option>
            <option value="__none__">Sin gremio</option>
            {gremioOptions.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Estado</span>
          <select
            className={selectCls}
            value={draft.status}
            onChange={(e) =>
              setDraft((d) => ({ ...d, status: e.target.value as DerivedClientStatus | 'all' }))
            }
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Familiares</span>
          <select
            className={selectCls}
            value={draft.family}
            onChange={(e) => setDraft((d) => ({ ...d, family: e.target.value as FamilyCountFilter }))}
          >
            {FAMILY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Ordenar por</span>
          <div className="flex gap-2">
            <select
              className={`${selectCls} flex-1`}
              value={draft.sortField}
              onChange={(e) =>
                setDraft((d) => ({ ...d, sortField: e.target.value as ClientSortField }))
              }
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() =>
                setDraft((d) => ({ ...d, sortDir: d.sortDir === 'asc' ? 'desc' : 'asc' }))
              }
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#E5EAF1] bg-white text-slate-600 transition hover:bg-slate-50 active:scale-95"
              title={draft.sortDir === 'asc' ? 'Ascendente' : 'Descendente'}
            >
              <ArrowsUpDownIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </BottomSheet>
  );
};

export default ClientFiltersSheet;
